import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Leaf, User, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useStaffAccess } from "@/hooks/useStaffAccess";
import { toast } from "sonner";
const navLinks = [{
  href: "/",
  label: "Home"
}, {
  href: "/browse",
  label: "Browse"
}, {
  href: "/pricing",
  label: "Pricing"
}, {
  href: "/about",
  label: "About"
}];
export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    user,
    profile,
    signOut
  } = useAuth();
  const { hasAccess, loading: staffLoading } = useStaffAccess();
  const handleSignOut = async () => {
    await signOut();
    toast.success("Signed out successfully");
    setIsOpen(false);
    window.location.href = "/";
  };

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);
  const getDashboardLink = () => {
    if (!staffLoading && hasAccess) return "/dashboard";
    if (!profile) return "/dashboard";
    switch (profile.role) {
      case "customer":
        return "/customer/bookings";
      case "provider":
        return "/practitioner/schedule";
      default:
        return "/dashboard";
    }
  };
  return <>
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none"
      >
        Skip to main content
      </a>
      
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-b border-border/50" aria-label="Main navigation">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-2 group touch-manipulation flex-shrink-0">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-wellness-sm group-hover:shadow-wellness-glow transition-shadow duration-300">
                <Leaf className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-display text-lg md:text-xl font-semibold text-foreground hidden sm:block">Wellness Connect</span>
            </Link>

            <div className="hidden lg:flex items-center justify-center flex-1">
              <div className="flex items-center gap-6 xl:gap-8">
                {navLinks.map(link => <Link key={link.href} to={link.href} className={cn("text-sm font-medium transition-colors duration-200 hover:text-primary", location.pathname === link.href ? "text-primary" : "text-muted-foreground")}>
                    {link.label}
                  </Link>)}
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-2 xl:gap-3 flex-shrink-0">
              {user ? <>
                  <Button variant="hero" size="sm" asChild className="lg:px-3 xl:px-4">
                    <Link to={getDashboardLink()}>
                      <User className="w-4 h-4 mr-2" />
                      Dashboard
                    </Link>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleSignOut} className="lg:px-3 xl:px-4">
                    <LogOut className="w-4 h-4 lg:mr-1 xl:mr-2" />
                    <span className="hidden xl:inline">Sign Out</span>
                    <span className="xl:hidden">Logout</span>
                  </Button>
                </> : <>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/auth?mode=login">Sign In</Link>
                  </Button>
                  <Button variant="hero" size="sm" asChild>
                    <Link to="/auth?mode=signup">Get Started</Link>
                  </Button>
                </>}
            </div>

            <div className="lg:hidden flex items-center gap-2">
              <button className="w-12 h-12 flex items-center justify-center rounded-xl hover:bg-secondary transition-colors touch-manipulation" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu" aria-expanded={isOpen}>
                {isOpen ? <X className="w-6 h-6 text-foreground" /> : <Menu className="w-6 h-6 text-foreground" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="h-16 md:h-20" />

      {isOpen && <div className="fixed inset-0 top-16 z-[100] bg-background lg:hidden">
          <div className="flex flex-col h-full px-4 py-6 overflow-y-auto">
            <div className="flex flex-col gap-3">
              {navLinks.map(link => <Link key={link.href} to={link.href} onClick={() => setIsOpen(false)} className={cn("px-5 py-4 rounded-xl text-lg font-medium transition-colors duration-200 touch-manipulation active:scale-[0.98]", location.pathname === link.href ? "bg-primary text-primary-foreground" : "text-foreground bg-secondary hover:bg-secondary/80")}>
                  {link.label}
                </Link>)}
            </div>
            
            <div className="flex flex-col gap-3 mt-8 pt-6 border-t border-border">
              {user ? <>
                  <Button variant="hero" asChild className="w-full h-14 text-base font-semibold touch-manipulation">
                    <Link to={getDashboardLink()} onClick={() => setIsOpen(false)}>
                      <User className="w-5 h-5 mr-2" />
                      My Dashboard
                    </Link>
                  </Button>
                  <Button variant="outline" onClick={handleSignOut} className="w-full h-14 text-base font-semibold touch-manipulation">
                    <LogOut className="w-5 h-5 mr-2" />
                    Sign Out
                  </Button>
                </> : <>
                  <Button variant="outline" asChild className="w-full h-14 text-base font-semibold touch-manipulation">
                    <Link to="/auth?mode=login" onClick={() => setIsOpen(false)}>
                      Sign In
                    </Link>
                  </Button>
                  <Button variant="hero" asChild className="w-full h-14 text-base font-semibold touch-manipulation">
                    <Link to="/auth?mode=signup" onClick={() => setIsOpen(false)}>
                      Get Started Free
                    </Link>
                  </Button>
                </>}
            </div>
          </div>
        </div>}
    </>;
}
